/**
 * Microphone-failure diagnostics for the in-app meeting bot.
 *
 * WHY THIS EXISTS: an in-app call can connect, negotiate a `sendrecv` audio
 * transceiver, play bot audio — and never have acquired a microphone track. The
 * agent talks, cannot hear you, and nothing anywhere logs an error. Three
 * independent mechanisms conspire to hide it:
 *
 *  1. `SmallWebRTCTransport` adds the audio transceiver unconditionally, then
 *     attaches a track only `if (audioTrack)`. A trackless session is a valid
 *     session as far as both peers are concerned.
 *  2. Daily (the default media manager) reports device failure as a
 *     `camera-error` event routed to Pipecat's `onDeviceError` callback, rather
 *     than by rejecting `initDevices()`. Nothing throws.
 *  3. `PipecatClient.connect()` awaits `initDevices()` OUTSIDE its own
 *     try/catch, so on the paths where it *does* throw, the promise `connect()`
 *     returned never settles — the caller's `catch` is unreachable.
 *
 * On top of that, a Permissions-Policy block and a user denial both surface as a
 * bare `NotAllowedError: Permission denied`. They need very different fixes
 * ("open the call in its own window" vs "allow the mic in site settings"), so
 * `watchPermissionsPolicyViolations` reads Chrome's `permissions-policy-violation`
 * report to tell them apart.
 *
 * Observed in Microsoft Fabric: the workload renders two cross-origin iframes
 * deep, and the portal's outer `<iframe allow="clipboard-read; clipboard-write">`
 * does not delegate `microphone`. Permissions Policy is inherited, so the nested
 * board frame can never obtain the mic no matter what its own `allow` says, and
 * the user is never prompted.
 */

/** Feature name as it appears in a `permissions-policy-violation` report body. */
export const MICROPHONE_FEATURE = 'microphone'

export type MicFailureReason =
  /** Permissions Policy: this frame was never permitted to even ask. */
  | 'blocked-by-embedder'
  /** The user (or a site setting) refused the prompt. */
  | 'denied'
  /** No input device present. */
  | 'not-found'
  /** Device held exclusively by another application. */
  | 'in-use'
  /** `navigator.mediaDevices` missing — typically an insecure context. */
  | 'unsupported'
  | 'unknown'

export interface MicFailure {
  reason: MicFailureReason
  /** Technical detail for logs. Never rendered to the user verbatim. */
  detail: string
}

/**
 * Structural shape of a Pipecat `DeviceError`. Typed structurally rather than
 * imported so this module stays free of the SDK (and trivially testable).
 */
export interface DeviceErrorLike {
  type?: string
  devices?: readonly string[]
  message?: string
  details?: Record<string, unknown>
}

const isDeviceErrorLike = (err: unknown): err is DeviceErrorLike =>
  typeof err === 'object' && err !== null && typeof (err as DeviceErrorLike).type === 'string'

const describe = (err: unknown): string => {
  if (isDeviceErrorLike(err)) {
    const blockedBy = err.details?.blockedBy
    return [
      `type=${err.type}`,
      err.devices?.length ? `devices=${err.devices.join(',')}` : null,
      blockedBy ? `blockedBy=${String(blockedBy)}` : null,
      err.message ? `message=${err.message}` : null,
    ]
      .filter(Boolean)
      .join(' ')
  }
  if (err instanceof Error) return `${err.name}: ${err.message}`
  return String(err)
}

/**
 * Map any mic acquisition failure — a Pipecat `DeviceError` or a raw
 * `DOMException` from `getUserMedia` — onto an actionable reason.
 *
 * `policyBlocked` should be true when a `permissions-policy-violation` report
 * for `microphone` has been seen in this document. It takes precedence: the
 * embedder's refusal is the root cause, and everything downstream of it reports
 * as a generic denial.
 */
export function classifyMicFailure(
  err: unknown,
  opts: { policyBlocked?: boolean } = {}
): MicFailure {
  const detail = describe(err)

  if (opts.policyBlocked) {
    return { reason: 'blocked-by-embedder', detail }
  }

  if (isDeviceErrorLike(err)) {
    switch (err.type) {
      case 'permissions':
        // Daily distinguishes the two cases we care about: 'browser' means the
        // platform refused without asking, 'user' means a dismissed prompt.
        return {
          reason: err.details?.blockedBy === 'browser' ? 'blocked-by-embedder' : 'denied',
          detail,
        }
      case 'not-found':
        return { reason: 'not-found', detail }
      case 'in-use':
        return { reason: 'in-use', detail }
      case 'undefined-mediadevices':
        return { reason: 'unsupported', detail }
      default:
        return { reason: 'unknown', detail }
    }
  }

  const name = err instanceof Error ? err.name : ''
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return { reason: 'denied', detail }
    case 'NotFoundError':
    case 'OverconstrainedError':
      return { reason: 'not-found', detail }
    case 'NotReadableError':
    case 'AbortError':
      return { reason: 'in-use', detail }
    case 'TypeError':
      return { reason: 'unsupported', detail }
    default:
      return { reason: 'unknown', detail }
  }
}

const I18N_KEYS: Record<MicFailureReason, string> = {
  'blocked-by-embedder': 'digital_coworker:meetingBot.micBlockedByEmbedder',
  denied: 'digital_coworker:meetingBot.micDenied',
  'not-found': 'digital_coworker:meetingBot.micNotFound',
  'in-use': 'digital_coworker:meetingBot.micInUse',
  unsupported: 'digital_coworker:meetingBot.micUnsupported',
  unknown: 'digital_coworker:meetingBot.micUnknown',
}

/** i18n key for the user-facing explanation of a failure reason. */
export function micFailureI18nKey(reason: MicFailureReason): string {
  return I18N_KEYS[reason]
}

/**
 * Re-mark an already-classified failure as an embedder block.
 *
 * Needed because the two signals race: `getUserMedia` rejects on a microtask
 * while the `permissions-policy-violation` report is delivered on a task, so a
 * policy-blocked call is often classified as `denied` BEFORE we learn the
 * embedder is the cause. Left alone, the user is told to fix it in site
 * settings — advice that cannot possibly work — which is exactly the confusion
 * this module exists to remove.
 */
export function asEmbedderBlock(failure: MicFailure): MicFailure {
  if (failure.reason === 'blocked-by-embedder') return failure
  return { reason: 'blocked-by-embedder', detail: failure.detail }
}

export const MIC_TRACK_WAIT_MS = 2000
export const MIC_TRACK_POLL_MS = 150

/**
 * Poll for a local audio track, up to `timeoutMs`.
 *
 * Daily can populate the track just after `initDevices()` resolves, so asserting
 * it immediately produces false negatives on healthy calls — which then stick as
 * a permanent "microphone unavailable" banner and a bogus analytics event. Wait
 * before concluding anything.
 *
 * Takes a probe rather than a client so this module stays SDK-free.
 */
export async function waitForMicTrack(
  hasTrack: () => boolean,
  opts: { timeoutMs?: number; pollMs?: number } = {}
): Promise<boolean> {
  const { timeoutMs = MIC_TRACK_WAIT_MS, pollMs = MIC_TRACK_POLL_MS } = opts
  const deadline = Date.now() + timeoutMs

  for (;;) {
    if (hasTrack()) return true
    if (Date.now() >= deadline) return false
    await new Promise((resolve) => setTimeout(resolve, pollMs))
  }
}

/** True when this context can request media at all (https / localhost). */
export function hasMediaDeviceSupport(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
}

/**
 * Watch for Permissions-Policy violations on the given features.
 *
 * `buffered: true` matters: Chrome queues reports emitted before the observer
 * attached, and the violation fires the moment the SDK first touches
 * `getUserMedia` — which can be earlier than any React effect.
 *
 * Returns a disconnect function. No-ops where `ReportingObserver` is
 * unavailable (Safari, jsdom), so callers need no feature guard of their own.
 */
export function watchPermissionsPolicyViolations(
  features: readonly string[],
  onViolation: (feature: string) => void
): () => void {
  if (typeof ReportingObserver === 'undefined') {
    return () => {}
  }

  const observer = new ReportingObserver(
    (reports) => {
      for (const report of reports) {
        if (report.type !== 'permissions-policy-violation') continue
        const feature = (report.body as { featureId?: string } | null)?.featureId
        if (feature && features.includes(feature)) {
          onViolation(feature)
        }
      }
    },
    { types: ['permissions-policy-violation'], buffered: true }
  )

  observer.observe()
  return () => observer.disconnect()
}
